// frontend-react/src/hooks/useKeeperProjections.js
import { useState, useEffect, useCallback } from 'react';
import keeperProjectionService from '../services/keeperProjectionService';

/**
 * Custom hook for managing keeper projection state and API integration
 * Handles projecting keepers into next season with contract extensions
 * 
 * @param {string} leagueId - League ID
 * @param {string} teamId - Team ID
 * @param {boolean} shouldLoad - Whether to load data (default: true)
 * @returns {object} Keeper projection state and actions
 */
export const useKeeperProjections = (leagueId, teamId, shouldLoad = true) => {
  // Data state
  const [eligibleKeepers, setEligibleKeepers] = useState([]);
  const [selectedKeepers, setSelectedKeepers] = useState([]);
  const [status, setStatus] = useState(null);
  const [deadMoney, setDeadMoney] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  
  // Local selection state (tracks projected keeper selections)
  const [localSelections, setLocalSelections] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  /**
   * Load all keeper projection data from API
   */
  const loadData = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setLoading(true);
      setError(null);

      // Load eligible keepers, saved selections, and status in parallel
      const [eligibleResponse, selectionsResponse, statusResponse] = await Promise.all([
        keeperProjectionService.getProjectedEligibleKeepers(leagueId, teamId),
        keeperProjectionService.getProjectedKeeperSelections(leagueId, teamId),
        keeperProjectionService.getProjectedStatus(leagueId, teamId)
      ]);

      console.log('Keeper projection data loaded:', {
        eligible: eligibleResponse,
        selections: selectionsResponse,
        status: statusResponse
      });

      // Set eligible keepers with projected 2026 info
      if (eligibleResponse.success && eligibleResponse.eligible_keepers) {
        setEligibleKeepers(eligibleResponse.eligible_keepers);
      }

      // Set saved keeper selections
      if (selectionsResponse.success && selectionsResponse.keeper_selections) {
        setSelectedKeepers(selectionsResponse.keeper_selections);
        
        // Initialize localSelections from saved selections
        const savedSelections = selectionsResponse.keeper_selections
          .filter(s => s.keeper?.status === 'saved' || s.keeper?.status === 'final')
          .map(selection => ({
            player_id: selection.player_id,
            keeper_cost: selection.keeper_cost,
            contract_years_remaining: selection.contract_years_remaining
          }));
        setLocalSelections(savedSelections);
      }

      // Set status
      if (statusResponse.success) {
        setStatus(statusResponse);
      }

      setIsDirty(false);
    } catch (err) {
      console.error('Error loading keeper projection data:', err);
      setError(err.message || 'Failed to load keeper projection data');
    } finally {
      setLoading(false);
    }
  }, [leagueId, teamId]);

  /**
   * Load keeper selection history
   */
  const loadHistory = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      const response = await keeperProjectionService.getKeeperHistory(leagueId, teamId);
      if (response.success && response.history) {
        setHistory(response.history);
      }
    } catch (err) {
      console.error('Error loading keeper history:', err);
    }
  }, [leagueId, teamId]);

  /**
   * Toggle keeper selection (add/remove from local state)
   * For projections, we need to track: player_id, keeper_cost, contract_years_remaining
   */
  const toggleKeeperSelection = useCallback((keeper) => {
    console.log('🎯 toggleKeeperSelection called with keeper:', keeper);
    console.log('🎯 BEFORE toggle - localSelections:', localSelections);

    setLocalSelections(prev => {
      console.log('🎯 INSIDE setLocalSelections - prev:', prev);
      // Check if keeper is already selected
      const existingIndex = prev.findIndex(k => k.player_id === keeper.player_id);
      
      if (existingIndex >= 0) {
        // Remove keeper
        const newSelections = [...prev];
        newSelections.splice(existingIndex, 1);
        setIsDirty(true);
        return newSelections;
      } else {
        // Check if we're at max keepers
        if (status && prev.length >= status.max_keepers) {
          console.warn('Cannot add more keepers - at maximum');
          return prev;
        }
        
        // Add keeper with projected values
        const projected = keeper.projected;
        const newKeeper = {
          player_id: keeper.player_id,
          keeper_cost: projected.keeper_cost,
          contract_years_remaining: projected.years_remaining
        };
        
        setIsDirty(true);
        return [...prev, newKeeper];
      }
    });
  }, [status]);

  /**
   * Calculate dead money for current selections
   */
  const calculateDeadMoneyForSelections = useCallback(async () => {
    if (!leagueId || !teamId || localSelections.length === 0) return;

    try {
      setCalculating(true);
      const selectedPlayerIds = localSelections.map(k => k.player_id);
      
      const response = await keeperProjectionService.calculateDeadMoney(
        leagueId, 
        teamId, 
        selectedPlayerIds
      );

      if (response.success) {
        setDeadMoney(response);
      }
    } catch (err) {
      console.error('Error calculating dead money:', err);
    } finally {
      setCalculating(false);
    }
  }, [leagueId, teamId, localSelections]);

  /**
   * Save projected keeper draft
   */
  const saveDraft = useCallback(async () => {
    if (!leagueId || !teamId || !isDirty) return;

    try {
      setSaving(true);
      setError(null);

      console.log('🔴 SAVE - localSelections being sent:', localSelections);
      console.log('🔴 SAVE - localSelections count:', localSelections.length);

      const response = await keeperProjectionService.saveProjectedKeeperDraft(
        leagueId,
        teamId,
        localSelections
      );

      console.log('Save projected draft response:', response);

      if (response.success) {
        // Refresh status without resetting localSelections
        // (loadData would replace localSelections with just what was saved, losing other players)
        const statusResponse = await keeperProjectionService.getProjectedStatus(leagueId, teamId);
        if (statusResponse.success) {
          setStatus(statusResponse);
        }

        // Refresh selectedKeepers to show saved state
        const selectionsResponse = await keeperProjectionService.getProjectedKeeperSelections(leagueId, teamId);
        if (selectionsResponse.success && selectionsResponse.keeper_selections) {
          setSelectedKeepers(selectionsResponse.keeper_selections);
        }

        setIsDirty(false);
      } else {
        throw new Error(response.message || 'Failed to save projected draft');
      }

      return response;
    } catch (err) {
      console.error('Error saving projected draft:', err);
      setError(err.message || 'Failed to save projected draft');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [leagueId, teamId, localSelections, isDirty, loadData]);

  /**
   * Finalize projected keeper selections (SUBMIT button)
   */
  const finalizeKeepers = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setFinalizing(true);
      setError(null);

      const response = await keeperProjectionService.finalizeProjectedKeepers(leagueId, teamId);

      console.log('Finalize response:', response);

      if (response.success) {
        // Reload data to get updated status
        await loadData();
      } else {
        throw new Error(response.message || 'Failed to finalize keepers');
      }

      return response;
    } catch (err) {
      console.error('Error finalizing keepers:', err);
      setError(err.message || 'Failed to finalize keepers');
      throw err;
    } finally {
      setFinalizing(false);
    }
  }, [leagueId, teamId, loadData]);

  /**
   * Clear all projected keeper selections (DESTRUCTIVE)
   */
  const clearAllSelections = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await keeperProjectionService.clearProjectedKeepers(leagueId, teamId);

      console.log('Clear response:', response);

      if (response.success) {
        setLocalSelections([]);
        setIsDirty(false);
        await loadData();
      } else {
        throw new Error(response.message || 'Failed to clear selections');
      }

      return response;
    } catch (err) {
      console.error('Error clearing selections:', err);
      setError(err.message || 'Failed to clear selections');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [leagueId, teamId, loadData]);

  /**
   * Copy current season keepers to projected season
   */
  const copyFromCurrent = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await keeperProjectionService.copyCurrentKeepers(leagueId, teamId);

      console.log('Copy response:', response);

      if (response.success) {
        await loadData();
      } else {
        throw new Error(response.message || 'Failed to copy keepers');
      }

      return response;
    } catch (err) {
      console.error('Error copying keepers:', err);
      setError(err.message || 'Failed to copy keepers');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [leagueId, teamId, loadData]);

  /**
   * Computed properties
   */
  const canSave = isDirty && !saving && !finalizing;
  const canFinalize = status && status.has_saved_selections && !status.can_submit;
  const selectedPlayerIds = localSelections.map(k => k.player_id);
  const selectedCount = localSelections.length;
  const totalSalary = localSelections.reduce((sum, k) => sum + (k.keeper_cost || 0), 0);

  // Load data on mount and when leagueId/teamId change (only if shouldLoad is true)
  useEffect(() => {
    if (shouldLoad) {
      loadData();
    }
  }, [loadData, shouldLoad]);

  // Auto-calculate dead money when selections change
  useEffect(() => {
    if (localSelections.length > 0) {
      calculateDeadMoneyForSelections();
    } else {
      setDeadMoney(null);
    }
  }, [localSelections, calculateDeadMoneyForSelections]);

  return {
    // Data
    eligibleKeepers,
    selectedKeepers,
    status,
    deadMoney,
    history,
    
    // Loading states
    loading,
    saving,
    finalizing,
    calculating,
    error,
    
    // Actions
    toggleKeeperSelection,
    saveDraft,
    finalizeKeepers,
    clearAllSelections,
    copyFromCurrent,
    refreshData: loadData,
    loadHistory,
    
    // Computed
    isDirty,
    canSave,
    canFinalize,
    selectedPlayerIds,
    localSelections,
    selectedCount,
    totalSalary
  };
};

export default useKeeperProjections;
